import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { message, notification } from "antd";
import Axios from "axios";

import DashboardView from "../../DashboardView";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import Loading from "../../../components/common/Spinners/Loading";
import Select from "../../../components/common/Select/Select";
import EditorSidebar from "./EditorSidebar";
import HttpResourceForm from "./HttpResourceForm";
import DbResourceForm from "./DbResourceForm";
import Icon from "./icons";
import { runEndpointTest } from "./testRequest";
import { validateAuth, validateBodyPresence, validateRawBody, validateRows } from "./validation";
import { AUTH_TYPES, TYPE_REGISTRY, dbSchema, typeKey } from "./resourceTypes";
import {
  connectionFromResource,
  connectionToPayload,
  emptyConnection,
  emptyResourceItem,
  resourceItemFromRecord,
  resourceToPayload,
  resourceTypeRecord,
  typeMetaFor,
} from "./payload";
import { checkResourceDuplicate, findDuplicateNames } from "./resourceApi";
import {
  deletetResourceAsync,
  getResourcesAsync,
  getResourceTypesAsync,
  resetResourceDeleteStatus,
} from "../../../redux/resourceSlice";
import { selectLicensedApps } from "../../../redux/userSlice";
import { licensedAppIdFor, reportAppIdFor } from "../../../utils/appAliases";
import { hasChanges } from "../../../utils/hasChanges";
import "./Editor.css";

/* The parts of `current` (today's errors) that were also wrong in `snapshot`
   (the errors at the last save). Error objects nest — {params: {0: {field,
   message}}, auth: {password: "…"}, url: "…"} — so this walks both together. A
   leaf (a string, or a row's {field, message}) is taken from `current`, so the
   message shown is today's wording. */
const keepStillWrong = (snapshot, current) => {
  if (!snapshot || !current) return undefined;
  if (typeof current !== "object" || typeof snapshot !== "object" || "message" in current) return current;
  const out = {};
  Object.keys(snapshot).forEach((key) => {
    const kept = keepStillWrong(snapshot[key], current[key]);
    if (kept !== undefined) out[key] = kept;
  });
  return Object.keys(out).length ? out : undefined;
};

/**
 * Resource editor — create and edit in one page.
 *
 *   /resources/new?type=restapi     new resource of that connection type
 *   /resources/:resourceId/edit     edit an existing one
 *
 * Two layouts, chosen by the connection type (see resourceTypes.js):
 *
 *   REST API / GraphQL   one form per resource, with the sidebar listing them.
 *                        Creating, that list is the resources about to be made:
 *                        fill in three, press Create, and three separate
 *                        resources are posted and appear as three rows in the
 *                        list. Editing, only the resource in the URL is changed
 *                        and the sidebar lists the application's others to
 *                        navigate between.
 *
 *   Mongo DB             the same, with a connection form instead of a URL
 *                        builder — several connections can be filled in and
 *                        created together too.
 *
 * One record is one resource is one URL, which is the model the rest of the app
 * is built on — a widget points at a resource by record id and reads one
 * baseUrl from it. See payload.js.
 *
 * The previous flow is untouched: /add-resource, ResourceTypesList and the three
 * antd modal forms all still work and still write the same records.
 */

const { REACT_APP_CONNECTION_INFO_BASE_URL } = process.env;

const IDLE_TEST = { status: "idle", message: "", detail: "" };

/* The shared Select works in { label, value } pairs. */
const TYPE_OPTIONS = TYPE_REGISTRY.map((type) => ({ label: type.label, value: type.key }));

export default function ResourceEditor() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  /* The ← arrow behaves like the browser's Back. It used to push /resources,
     which added a page instead of leaving one — so pressing browser Back
     afterwards returned to the editor. `location.key` is only set for an entry
     React Router created, i.e. the user got here from inside the app; a direct
     link or a fresh tab has nothing of ours to go back to, so it opens the list. */
  const goBack = () => {
    if (location.key) history.goBack();
    else history.push("/resources");
  };
  const { resourceId } = useParams();
  const isEdit = Boolean(resourceId);

  const resources = useSelector((state) => state.resources);
  /* Only the applications this user holds a licence for — a special user gets them all.
     The registry returns every application Witmeg has, and offering all of them here let
     a resource be filed under one the customer cannot use. Same list the Create Schedule
     page offers; see selectLicensedApps in redux/userSlice.js. */
  const apps = useSelector(selectLicensedApps);

  const resourceData = useMemo(() => resources.resourceData || [], [resources.resourceData]);
  const resourceTypes = resources.resourceTypes || [];
  const isListLoading = resources.resourceStatus === "pending" || resources.resourceStatus === "default";

  /* The type comes from the query when creating and from the record when
     editing, so an edit URL needs no query string on it. */
  const queryType = new URLSearchParams(location.search).get("type");
  const existing = useMemo(
    () => (isEdit ? resourceData.find((r) => r.id === resourceId) : null),
    [isEdit, resourceData, resourceId],
  );

  /* Same rule as the Resources list: a Witmeg API is read-only unless you are a
     special user. The list disables Edit and Delete for these, but the editor
     did not check at all — reached by URL (or the old enabled Edit button) it
     let anyone update or delete one. Sent back to the list instead. */
  const isLockedRecord =
    Boolean(existing?.isWitmegApi) && localStorage.getItem("IsSpecialUser") !== "true";

  // useEffect(() => {
  //   if (!isLockedRecord) return;
  //   message.warning("Witmeg resources are read-only.");
  //   history.replace("/resources");
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isLockedRecord]);

  /* View mode (?mode=view) — the list's eye icon. Every field is locked and
     there is no Update or Delete; the header's Edit button switches to editing,
     and is only there for users allowed to edit this record. A locked record is
     always viewed, whatever the URL asks for, so it can be read but not changed. */
  const viewRequested = new URLSearchParams(location.search).get("mode") === "view";
  const isReadOnly = isEdit && (viewRequested || isLockedRecord);

  // Keep the URL honest: a locked record reached on its edit URL shows ?mode=view.
  useEffect(() => {
    if (isLockedRecord && !viewRequested) history.replace(`/resources/${resourceId}/edit?mode=view`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLockedRecord, viewRequested]);

  const startEditing = () => {
    if (isLockedRecord) return;
    history.replace(`/resources/${resourceId}/edit`);
  };

  const activeTypeKey = isEdit
    ? typeKey(existing?.icon || existing?.resoueceType) || "restapi"
    : typeKey(queryType) || "restapi";

  const typeMeta = typeMetaFor(activeTypeKey);
  const layout = typeMeta.layout;
  const schema = dbSchema(activeTypeKey);

  /* ── Form state ───────────────────────────────────────────── */
  const [appId, setAppId] = useState("");

  /* The resources this page is working on — HTTP resources or connections,
     whichever the type is. Creating, that is one or more drafts to be posted;
     editing, it is exactly the one record in the URL. Both layouts work the
     same way, so a Mongo page can build several connections at once too. */
  const [items, setItems] = useState(() => [emptyResourceItem()]);
  const [selectedItemId, setSelectedItemId] = useState(null);

  const [test, setTest] = useState(IDLE_TEST);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  // Every item's errors as they stood at the last save attempt: item id → errors.
  const [errorSnapshot, setErrorSnapshot] = useState(() => new Map());
  /* What the server said about each name it has been asked about:
     name → { taken: boolean, message: string }.

     Keyed by the name, and never cleared when a name is re-entered. An earlier
     version threw the answers away on every rename and only re-asked when the
     name CHANGED — so committing the same name a second time wiped the answer
     and nothing fetched it again, which is why a known-duplicate name showed
     nothing. Keyed this way the answer belongs to the name, so it survives any
     amount of clicking about and a name already asked about needs no second
     request.

     The message is kept alongside because the server words it, and that is what
     the user should read rather than a second version written here. */
  const [nameChecks, setNameChecks] = useState(new Map());
  // The name a request is in flight for, so only that field shows a spinner.
  const [checkingFor, setCheckingFor] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null); // { kind, itemId?, resource?, label, saved }
  /* Editing only: the application and resource exactly as they were seeded, so
     Update can stay disabled until something is actually changed. */
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  useEffect(() => {
    if (!resourceData.length) dispatch(getResourcesAsync());
    if (!resourceTypes.length) dispatch(getResourceTypesAsync());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // The list is refetched after a delete so the sidebar and the list page agree.
  useEffect(() => {
    if (resources.resourceDeleteStatus === "success") {
      dispatch(resetResourceDeleteStatus());
      dispatch(getResourcesAsync());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resources.resourceDeleteStatus]);

  /* Seed the form from the record being edited. Keyed on the id so a later
     refetch of the resource list does not throw away what has been typed.

     A new resource is keyed on its type as well, so changing the connection
     type starts a fresh form — the fields and the default port belong to the
     type, and carrying the old ones over would leave a Mongo form with no port
     in it. */
  const seededFor = useRef(null);
  useEffect(() => {
    if (!isEdit) {
      const key = `new:${activeTypeKey}`;
      if (seededFor.current === key) return;
      seededFor.current = key;
      const first =
        typeMetaFor(activeTypeKey).layout === "http" ? emptyResourceItem() : emptyConnection(dbSchema(activeTypeKey));
      setItems([first]);
      setSelectedItemId(first.id);
      setAppId((current) => current || apps[0]?.ApplicationID || "");
      setTest(IDLE_TEST);
      return;
    }

    if (!existing || seededFor.current === resourceId) return;
    seededFor.current = resourceId;

    /* Through licensedAppIdFor: a resource stores the aliased application id while the
       select offers the licensed one, and those differ for the Neurolage pair. Without it
       an existing resource opened with Application blank. See utils/appAliases.js. */
    // setAppId(licensedAppIdFor(existing.appId || ""));
    const seededAppId = licensedAppIdFor(existing.appId || "");
    setAppId(seededAppId);

    /* Editing is one resource — the one in the URL. Nothing else is loaded, so
       pressing the button can only ever update that record. */
    const existingType = typeKey(existing.icon || existing.resoueceType);
    const item =
      typeMetaFor(existingType).layout === "http"
        ? resourceItemFromRecord(existing)
        : connectionFromResource(existing, dbSchema(existingType));
    setItems([item]);
    setSelectedItemId(item.id);
    setInitialSnapshot({ appId: seededAppId, items: [item] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existing, resourceId, resourceData, apps, activeTypeKey]);

  /* Creating is always "changed" — there is nothing to compare against. Editing,
     Update waits for a real difference from what was loaded; typing a character
     and deleting it again reads as unchanged. Before the record has been seeded
     there is nothing to save either. */
  const isDirty = useMemo(
    () => (isEdit ? initialSnapshot !== null && hasChanges(initialSnapshot, { appId, items }) : true),
    [isEdit, initialSnapshot, appId, items],
  );

  /* ── Derived ──────────────────────────────────────────────── */
  const appOptions = useMemo(() => apps.map((app) => ({ label: app.Name, value: app.ApplicationID })), [apps]);

  /* What gets stored against the resource, which is not always what the select holds:
     picking "Web Neurolage-WholeSale" files the resource under "Neurolage", so it lands
     where the reports and the ERP site look for it. `appId` itself stays the licensed id
     because that is what the dropdown and the filtering above are keyed on. */
  const storedAppId = reportAppIdFor(appId);

  const selectedItem = items.find((it) => it.id === selectedItemId) || items[0] || null;

  /* Changing the connection type pushes a new URL rather than remounting, so
     `layout` flips a render before the seeding effect replaces `items`. For that
     one frame the page is an HTTP form holding a connection, or the other way
     round — and the HTTP form reading `.method.toLowerCase()` off a connection
     throws. Anything layout-specific is held back until the two agree; the
     effect lands immediately after, so nothing is visible but the crash. */
  const itemsMatchLayout = !selectedItem || selectedItem.kind === layout;
  const activeItem = itemsMatchLayout ? selectedItem : null;

  /* ── The sidebar's list while editing ──────────────────────
     Just the record being edited. Editing is one resource, and a list of the
     others invites the question of what happens when you click one — so for now
     it shows only what was opened.

     PREVIOUS VERSION — every resource of the same type in the same application,
     clicking one to move to its edit page. It worked; it is more than the page
     needs today. Kept rather than deleted, per house convention. Restoring it
     needs `reportAppIdsFor` added back to the appAliases import.

  const showsExisting = layout === "db" || isEdit;
  const siblingResources = useMemo(() => {
    if (!showsExisting) return [];
    return resourceData
      .filter((r) => typeKey(r.icon || r.resoueceType) === activeTypeKey)
      // every id that counts as the chosen application, so an aliased pair reads as one
      .filter((r) => !appId || reportAppIdsFor(appId).includes(r.appId))
      .map((r) => ({
        id: r.id,
        name: r.name || "Untitled",
        // Drives the method badge; a connection has none and gets the db glyph.
        method: r.connectionData?.requestValues?.httpMethod
          ? String(r.connectionData.requestValues.httpMethod).toUpperCase()
          : null,
        resource: r,
      }));
  }, [showsExisting, resourceData, activeTypeKey, appId]);
  ───────────────────────────────────────────────────────────── */
  // Only read by the commented-out list-mode sidebar further down; kept with it.
  // eslint-disable-next-line no-unused-vars
  const sidebarItems = useMemo(() => {
    if (!isEdit || !existing) return [];
    return [
      {
        id: existing.id,
        name: existing.name || "Untitled",
        // Drives the method badge; a connection has none and gets the db glyph.
        method: existing.connectionData?.requestValues?.httpMethod
          ? String(existing.connectionData.requestValues.httpMethod).toUpperCase()
          : null,
        resource: existing,
      },
    ];
  }, [isEdit, existing]);

  /* ── Resource editing ─────────────────────────────────────── */
  const patchItem = (patch) => {
    setItems((list) => list.map((it) => (it.id === activeItem?.id ? { ...it, ...patch } : it)));
    /* Nothing to clear: answers are held against the name they are about, so
       typing a different one simply has no answer yet and the check runs for
       it. Clearing here is what used to lose a result the moment the same name
       was committed twice. */
  };

  const selectItem = (itemId) => {
    setSelectedItemId(itemId);
    // A result belongs to the resource it was run for, so it does not carry over.
    setTest(IDLE_TEST);
  };

  const addItem = () => {
    const item = layout === "http" ? emptyResourceItem() : emptyConnection(schema);
    setItems((list) => list.concat(item));
    selectItem(item.id);
  };

  const renameItem = (itemId, name) => {
    setItems((list) => list.map((it) => (it.id === itemId ? { ...it, name } : it)));
  };

  /* Removing a draft is local — nothing has been created yet. The last one
     cannot go, or the page would have no form on it. */
  const requestDeleteItem = (itemId) => {
    const item = items.find((it) => it.id === itemId);
    if (!item) return;
    if (items.length === 1) {
      message.info("There has to be at least one resource on the page.");
      return;
    }
    // Nothing typed — no need to ask. `url` for an HTTP draft, `host` for a connection.
    if (!String(item.name || "").trim() && !String(item.url || item.host || "").trim()) {
      dropItem(itemId);
      return;
    }
    setPendingDelete({ kind: "draft", itemId, label: item.name || "Untitled" });
  };

  const dropItem = (itemId) => {
    const next = items.find((it) => it.id !== itemId);
    setItems((list) => list.filter((it) => it.id !== itemId));
    if (selectedItemId === itemId && next) selectItem(next.id);
  };

  const confirmDelete = () => {
    const request = pendingDelete;
    setPendingDelete(null);
    if (!request) return;

    if (request.kind === "draft") {
      dropItem(request.itemId);
      return;
    }

    // An existing record: it has to go through the API or it returns on the next refetch.
    (request.saved || []).forEach((id) => dispatch(deletetResourceAsync({ id })));

    // Removing the resource currently open leaves the page on a blank form.
    // if (request.resource?.id === resourceId) history.push(`/resources/new?type=${activeTypeKey}`);
    // Replace: the deleted record's edit page must not be one Back press away.
    // if (request.resource?.id === resourceId) history.replace(`/resources/new?type=${activeTypeKey}`);
    /* Back to the list, not a blank form. The list page takes it from there: it
       shows "Resource has been successfully deleted!" when the delete lands and
       refetches, so the record is gone from it. Replace rather than push, so
       browser Back cannot return to the deleted record ("could not be found"). */
    if (request.resource?.id === resourceId) history.replace("/resources");
  };

  /* ── Test ─────────────────────────────────────────────────── */
  const handleTest = async () => {
    if (!activeItem) return;
    setTest({ status: "pending", message: "", detail: "" });
    const result = await runEndpointTest(activeItem);
    setTest(result);
  };

  /* ── Validation ───────────────────────────────────────────── */

  /* Only the table in use is validated: a body the request does not send (None,
     or the mode the user is not on) has no rows to get wrong. */
  const bodyRowsOf = (endpoint) =>
    endpoint.body.mode === "formData"
      ? endpoint.body.formData
      : endpoint.body.mode === "urlencoded"
        ? endpoint.body.urlencoded
        : [];

  /* Same rule as the antd form's validateUrl: it has to parse, and it has to be
     http or https. A scheme the browser will not fetch — ftp:, file:, or a bare
     "api.example.com" with no scheme at all — is caught here rather than at the
     first run of the report. */
  const isValidUrl = (url) => {
    try {
      return ["http:", "https:"].indexOf(new URL(url).protocol) !== -1;
    } catch (e) {
      return false;
    }
  };

  /* Two resources being created together cannot share a name — the server would
     accept the first and reject the second, and each would blame the other. */
  const namesUsedTwice = useMemo(() => {
    const seen = new Set();
    const twice = new Set();
    items.forEach((it) => {
      const name = String(it.name || "")
        .trim()
        .toLowerCase();
      if (!name) return;
      if (seen.has(name)) twice.add(name);
      seen.add(name);
    });
    return twice;
  }, [items]);

  /**
   * What is wrong with this resource's name, if anything.
   *
   * Two separate rules, and both have to be here. The server only knows about
   * resources that have been saved, so it cannot see that two drafts on this
   * page were both called "Sales API" — they would both pass the check and then
   * be created as a pair of resources sharing a name. That one is the frontend's
   * to catch, and it is checked first because it is the one the user can see the
   * cause of.
   *
   * Compared case-insensitively, which is stricter than the server's answer: two
   * resources called "Sales API" and "sales api" are a mistake whatever the
   * backend would accept.
   */
  const nameErrorFor = (item) => {
    const name = String(item?.name || "").trim();
    if (!name) return "A name is required.";
    if (namesUsedTwice.has(name.toLowerCase())) return "Another resource on this page already has this name.";
    // The server's own wording, so the page says what the API says.
    const checked = nameChecks.get(name);
    if (checked?.taken) return checked.message || "A resource with this name already exists.";
    return null;
  };

  /* Row rules live in validation.js, shared with ParamsTable so the box that is
     marked red is the box that is stopping the save. */
  const itemErrors = (item) => {
    const errors = {};

    const nameProblem = nameErrorFor(item);
    if (nameProblem) errors.name = nameProblem;

    const url = String(item.url || "").trim();
    if (!url) {
      errors.url = "A URL is required.";
    } else if (!isValidUrl(url)) {
      errors.url = "Enter a valid URL starting with http:// or https://";
    }

    const params = validateRows(item.params);
    const headers = validateRows(item.headers);
    const body = validateRows(bodyRowsOf(item));
    if (Object.keys(params).length) errors.params = params;
    if (Object.keys(headers).length) errors.headers = headers;
    if (Object.keys(body).length) errors.body = body;

    // A body type other than None has to carry something.
    const bodyMissing = validateBodyPresence(item.body);
    if (bodyMissing) errors.bodyMissing = bodyMissing;

    // Raw text has to match its format — only when Raw is the body being sent.
    if (item.body?.mode === "raw" && !bodyMissing) {
      const raw = validateRawBody(item.body.rawFormat, item.body.rawBody);
      if (raw) errors.rawBody = raw;
    }

    // An auth type other than None needs all of its fields.
    const auth = validateAuth(item.auth, AUTH_TYPES);
    if (Object.keys(auth).length) errors.auth = auth;

    return errors;
  };

  /* The first thing standing between the page and being saved, as a sentence,
     plus which resource it is in. Every resource on the page is checked, not
     just the one on screen — a field marked red in a resource nobody is looking
     at helps no one, so the save names it and opens it. */
  const httpBlocker = useMemo(() => {
    // Nothing to say while the page is mid-swap between connection types.
    if (layout !== "http" || !itemsMatchLayout) return null;
    if (!appId) return { message: "Pick an application for this resource." };

    for (const item of items) {
      const problems = itemErrors(item);
      if (!Object.keys(problems).length) continue;

      const where = `“${item.name.trim() || "Untitled"}”`;
      if (problems.name) return { itemId: item.id, message: `${where}: ${problems.name}` };
      if (problems.url) return { itemId: item.id, message: `${where}: ${problems.url}` };
      // if (problems.rawBody && !problems.params && !problems.headers) {
      //   return { itemId: item.id, message: `${where} — Body: ${problems.rawBody.toLowerCase()}.` };
      // }
      // const tab = problems.params ? "Parameters" : problems.headers ? "Headers" : "Body";
      // const first = Object.values(problems.params || problems.headers || problems.body)[0];
      // return { itemId: item.id, message: `${where} — ${tab}: ${first.message.toLowerCase()}.` };
      /* In tab order. Row tables hold {index: {message}}, auth holds
         {field: message}, the body checks are plain strings — so each is read
         its own way rather than assuming every problem is a table row. */
      const firstRow = (rows) => Object.values(rows)[0].message;
      const tabProblem = problems.params
        ? ["Parameters", firstRow(problems.params)]
        : problems.auth
          ? ["Authentication", Object.values(problems.auth)[0]]
          : problems.headers
            ? ["Headers", firstRow(problems.headers)]
            : problems.bodyMissing
              ? ["Body", problems.bodyMissing]
              : problems.body
                ? ["Body", firstRow(problems.body)]
                : ["Body", problems.rawBody || ""];
      const [tab, detail] = tabProblem;
      return { itemId: item.id, message: `${where} — ${tab}: ${String(detail).toLowerCase()}` };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, items, namesUsedTwice, nameChecks, layout, itemsMatchLayout]);

  /* A connection's required fields, including its Name — which is one of the
     schema fields, so it needs no separate rule. Name clashes are checked the
     same way as an HTTP resource's, since both end up as records. */
  const connectionErrorsFor = (item) => {
    const errors = {};
    schema.fields.forEach((field) => {
      if (field.required && !String(item[field.key] || "").trim()) {
        errors[field.key] = `${field.label} is required.`;
      }
    });

    /* Same name rules as an HTTP resource — both end up as records, and a
       connection sharing a name with one is the same mistake. The "required"
       half is already covered by the schema loop above, so only a name that
       exists is worth asking about. */
    if (String(item.name || "").trim()) {
      const nameProblem = nameErrorFor(item);
      if (nameProblem) errors.name = nameProblem;
    }
    return errors;
  };

  /* The same idea for connections: the first missing required field, named,
     across every connection on the page. */
  const dbBlocker = useMemo(() => {
    // As above: silent while the connection type is being swapped.
    if (layout !== "db" || !itemsMatchLayout) return null;
    if (!appId) return { message: "Pick an application for this resource." };
    for (const item of items) {
      const problems = connectionErrorsFor(item);
      const first = Object.values(problems)[0];
      if (first) return { itemId: item.id, message: `“${item.name?.trim() || "Untitled"}”: ${first}` };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, items, schema, namesUsedTwice, nameChecks, layout, itemsMatchLayout]);

  /* Before a save is attempted nothing is marked — a form that starts out red is
     telling the user off for fields they have not reached yet — with one
     exception: a name that is already taken, either by a saved resource or by
     another draft on this page. Both are answers about something typed a moment
     ago rather than complaints about an empty field, and holding them until
     Create is pressed is the whole problem with checking only on save.

     "A name is required" is not shown early: an unnamed draft is one the user
     has not got to yet, not one they got wrong. */
  const nameOnly = (errors, item) => {
    const name = String(item?.name || "").trim();
    if (!errors.name || !name) return {};
    const answered = nameChecks.get(name)?.taken || namesUsedTwice.has(name.toLowerCase());
    return answered ? { name: errors.name } : {};
  };

  /* ── Is this name free? ───────────────────────────────────────
     Asked while the user types rather than only when they save — being told a
     name is taken after filling in the whole form is too late to be useful, and
     it is what the antd form does as you type in its Name field.

     Waits for a pause in typing: one request per name settled on, not one per
     keystroke. The id of the record being edited goes with it, so keeping a
     resource's existing name is not reported as a clash with itself.

     `cancelled` guards the reply: answers can arrive out of order, and a slow
     answer about a name that has since been typed over must not mark the field. */
  const activeName = String(activeItem?.name || "").trim();
  const activeResourceId = activeItem?.resourceId || null;
  const activeNameCheck = activeName ? nameChecks.get(activeName) : null;

  useEffect(() => {
    // Nothing typed, or this name has already been asked about.
    if (!activeName || nameChecks.has(activeName)) {
      setCheckingFor("");
      return undefined;
    }

    let cancelled = false;
    setCheckingFor(activeName);

    const timer = setTimeout(async () => {
      const { isDuplicate, message } = await checkResourceDuplicate(activeName, activeResourceId);
      if (cancelled) return;
      setCheckingFor("");
      setNameChecks((current) => new Map(current).set(activeName, { taken: isDuplicate, message }));
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeName, activeResourceId]);

  /* Each validator only for its own kind of item. Both memos run on every
     render whatever the type, and the HTTP one reading `item.body.mode` off a
     Mongo connection (which has no body) crashed the editor the moment
     "Mongo DB" was picked. */
  /* Validation is shown as of the last save attempt, not live. Showing `all`
     after the first save re-validated on every keystroke — typing a key into a
     fresh row flagged "Value is required" before the user reached the value.

     What is shown is the save-time snapshot narrowed to what is STILL wrong:
     fixing a field clears its message at once, but a problem that appears
     while typing waits for the next save. The name keeps its own live check
     (nameOnly) — "already taken" is an answer, not a complaint. */
  const shownErrors = (item, all) => {
    if (!showErrors) return nameOnly(all, item);
    return { ...(keepStillWrong(errorSnapshot.get(item.id), all) || {}), ...nameOnly(all, item) };
  };

  const activeConnectionErrors = useMemo(() => {
    // if (!activeItem) return {};
    if (!activeItem || activeItem.kind !== "db") return {};
    const all = connectionErrorsFor(activeItem);
    // return showErrors ? all : nameOnly(all, activeItem);
    return shownErrors(activeItem, all);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem, showErrors, errorSnapshot, schema, namesUsedTwice, nameChecks]);

  const activeItemErrors = useMemo(() => {
    // if (!activeItem) return {};
    if (!activeItem || activeItem.kind !== "http") return {};
    const all = itemErrors(activeItem);
    // return showErrors ? all : nameOnly(all, activeItem);
    return shownErrors(activeItem, all);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem, showErrors, errorSnapshot, namesUsedTwice, nameChecks]);

  /* Everything the name field has to show, in one object: a spinner while the
     server is being asked, a tick once it comes back free, a cross and the
     reason when it is not.

     Built from the computed errors rather than from the server's reply alone, so
     a name that clashes with another draft on this page — which the server
     cannot see — marks the field the same way a taken one does. */
  const activeNameError = (layout === "http" ? activeItemErrors : activeConnectionErrors).name || null;
  /* No application chosen, once a save has been attempted. Read by the picker in
     the top bar and by the one in the sidebar — they set the same value, so both
     have to show the same thing being wrong. */
  const appMissing = showErrors && !appId;

  const nameStatus = {
    checking: Boolean(activeName) && checkingFor === activeName,
    available: Boolean(activeNameCheck) && !activeNameCheck.taken && !activeNameError,
    error: activeNameError,
  };

  /* ── Save ─────────────────────────────────────────────────── */
  const postResource = useCallback(async (payload) => {
    const url = payload.id
      ? `${REACT_APP_CONNECTION_INFO_BASE_URL}/update-resource`
      : `${REACT_APP_CONNECTION_INFO_BASE_URL}/resource`;
    return Axios.post(url, payload, {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
    });
  }, []);

  /* The user-facing names of the fields holding up an item, in on-screen order.
     HTTP errors are keyed by area; a connection's are keyed by schema field, so
     their labels come from the schema. */
  const HTTP_FIELD_LABELS = {
    name: "Resource Name",
    url: "URL",
    params: "Parameters",
    auth: "Authentication",
    headers: "Headers",
    body: "Body",
    bodyMissing: "Body",
    rawBody: "Body",
  };
  const problemFieldLabels = (item) => {
    if (item.kind === "http") {
      const errors = itemErrors(item);
      const labels = Object.keys(HTTP_FIELD_LABELS)
        .filter((key) => errors[key])
        .map((key) => HTTP_FIELD_LABELS[key]);
      // Several body checks share the one "Body" label — name it once.
      return [...new Set(labels)];
    }
    const errors = connectionErrorsFor(item);
    return schema.fields.filter((field) => errors[field.key]).map((field) => field.label);
  };
  // "A", "A and B", "A, B and C"
  const joinLabels = (labels) =>
    labels.length <= 1 ? labels[0] || "" : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

  /* A save attempt is the moment validation is shown: every item's errors are
     recorded as they stand now, and that is what the fields display until the
     next attempt (see shownErrors). */
  const markSaveAttempt = () => {
    setErrorSnapshot(
      new Map(items.map((it) => [it.id, it.kind === "http" ? itemErrors(it) : connectionErrorsFor(it)])),
    );
    setShowErrors(true);
  };

  /* One save for both layouts: the only difference is which builder turns an
     item into a payload, and the blocker that guards it. */
  const save = async () => {
    // Viewing saves nothing (the button is not rendered; this covers any other path).
    if (isReadOnly) return;
    // The page is mid-swap between connection types; the blockers are silent, so
    // there is nothing meaningful to send yet.
    if (!itemsMatchLayout) return;

    const blocker = layout === "http" ? httpBlocker : dbBlocker;

    /* Pressing the button is how the user asks to be told what is wrong, so the
       first problem is named and its resource opened. */
    if (blocker) {
      // setShowErrors(true);
      markSaveAttempt();
      if (blocker.itemId) selectItem(blocker.itemId);
      // message.error(blocker.message);
      /* The fields themselves now say what is wrong (showErrors above), so the
         toast only points at them. Repeating the first field's message, prefixed
         with "Untitled" for an unnamed resource, read as a second, different
         error. Only when several resources are on the page is it worth naming
         which one was opened — and only by a name the user actually typed. */
      if (blocker.itemId) {
        const item = items.find((it) => it.id === blocker.itemId);
        const name = String(item?.name || "").trim();
        // message.error(
        //   items.length > 1 && name
        //     ? `Please fix the highlighted fields in “${name}”.`
        //     : "Please fix the highlighted fields.",
        // );
        /* Name the fields rather than "the highlighted fields" — read off the
           same error object that marks them, so the toast and the form agree. */
        const fields = item ? problemFieldLabels(item) : [];
        const list = joinLabels(fields);
        const where = items.length > 1 && name ? ` in “${name}”` : "";
        message.error(list ? `Please check ${list}${where}.` : `Please fix the highlighted fields${where}.`);
      } else {
        // No application picked — not a field of the resource, so say it outright.
        message.error(blocker.message);
      }
      return;
    }

    setSaving(true);
    try {
      /* Every name is checked before anything is created. Finding out that the
         third of three is taken after the first two exist would leave a mess
         with no transaction to undo it. */
      const taken = await findDuplicateNames(items);
      if (taken.size) {
        // Folded into what is already known, so the fields mark themselves.
        setNameChecks((current) => {
          const next = new Map(current);
          taken.forEach((reason, name) => next.set(name, { taken: true, message: reason }));
          return next;
        });
        // setShowErrors(true);
        markSaveAttempt();
        const first = items.find((it) => taken.has(String(it.name || "").trim()));
        if (first) selectItem(first.id);
        // taken is a Map of name → message, so the names come from its keys.
        const [firstName] = [...taken.keys()];
        message.error(
          taken.size === 1
            ? `“${firstName}” is already taken. Pick another name.`
            : `${taken.size} of these names are already taken.`,
        );
        return;
      }

      const typeRecord = resourceTypeRecord(resourceTypes, activeTypeKey);
      const toPayload = (item) =>
        layout === "http"
          ? resourceToPayload({ item, appId: storedAppId, typeMeta, typeRecord })
          : connectionToPayload({ connection: item, appId: storedAppId, typeMeta, typeRecord });

      /* One request per resource — there is no bulk route, so this is not
         atomic. Each is reported on its own: the ones that worked are gone from
         the page, and the ones that did not are still there to try again. */
      const failed = [];
      for (const item of items) {
        const payload = toPayload(item);
        try {
          // eslint-disable-next-line no-await-in-loop
          await postResource(payload);
        } catch (error) {
          failed.push({ item, reason: error.response?.data?.message || error.message });
        }
      }

      dispatch(getResourcesAsync());

      if (!failed.length) {
        message.success(
          isEdit
            ? "Resource updated."
            : items.length === 1
              ? "Resource created."
              : `${items.length} resources created.`,
        );
        // history.push("/resources");
        // Replace, so browser Back from the list does not reopen the form just saved.
        history.replace("/resources");
        return;
      }

      /* One toast per failure, naming the resource — "2 of 3 failed" leaves the
         user to work out which two. Bottom right, and each stays until it is
         dismissed: several can stack without covering the form, and a message
         about work that was lost should not time out before it is read.

         The failures stay on the page so they can be fixed and sent again; the
         successes are dropped so pressing the button twice cannot create them
         a second time. */
      failed.forEach(({ item, reason }) => {
        notification.error({
          message: `“${item.name.trim() || "Untitled"}” creating has failed`,
          description: reason,
          placement: "bottomRight",
          duration: 0,
        });
      });

      const failedIds = failed.map((f) => f.item.id);
      setItems((list) => list.filter((it) => failedIds.indexOf(it.id) !== -1));
      setSelectedItemId(failedIds[0]);
    } finally {
      setSaving(false);
    }
  };

  /* ── Render ───────────────────────────────────────────────── */
  // Leaving for the list (see isLockedRecord) — never render its form, even for a frame.
  // if (isLockedRecord) return null;
  // (Locked records are now viewed read-only instead — see isReadOnly.)

  if (isEdit && !existing) {
    return (
      <DashboardView>
        <div className="rsce-shell">
          {isListLoading ? (
            <Loading />
          ) : (
            <div className="rsce-missing">
              <p>That resource could not be found.</p>
              <button type="button" className="rsce-btn rsce-btn-primary" onClick={() => history.push("/resources")}>
                Back to Resources
              </button>
            </div>
          )}
        </div>
      </DashboardView>
    );
  }

  return (
    <DashboardView>
      <div className="rsce-shell">
        {/* Breadcrumb bar — back to the list, the resource name, and the two
            things that apply to the whole resource. */}
        <div className={`rsce-topbar ${appMissing ? "has-error" : ""}`}>
          <div className="rsce-topbar-left">
            {/* <button type="button" className="rsce-back" onClick={() => history.push("/resources")} title="Back"> */}
            <button type="button" className="rsce-back" onClick={goBack} title="Back">
              {Icon.back}
            </button>
            <button type="button" className="rsce-crumb-link" onClick={() => history.push("/resources")}>
              Resources
            </button>
            <span className="rsce-crumb-sep">{Icon.chevronRight}</span>

            {/* The crumb reflects the Name field on the form rather than being a
                second name to keep in step with it. Creating several at once,
                it says so instead of picking one of them to display. */}
            <span className="rsce-resource-name">
              {isEdit
                ? activeItem?.name || "Edit"
                : items.length > 1
                  ? `Add New — ${items.length} resources`
                  : activeItem?.name || "Add New"}
            </span>
          </div>

          <div className="rsce-topbar-right">
            {/* View mode → editing. Hidden, not disabled, for a record this user
                may not change — the same rule that disables Edit/Delete in the
                Resources list (a Witmeg API, and not a special user). */}
            {isReadOnly && !isLockedRecord && (
              <button type="button" className="rsce-btn rsce-btn-primary rsce-edit-btn" onClick={startEditing}>
                {Icon.pencil}
                Edit
              </button>
            )}

            {/* Changing type on an existing record would rewrite what it is, so
                it is fixed once saved. */}
            <Select
              className="rsce-type-pick"
              options={TYPE_OPTIONS}
              value={{ label: typeMeta.label, value: activeTypeKey }}
              disabled={isEdit}
              title={isEdit ? "A saved resource keeps its connection type" : "Connection type"}
              // onChange={(option) => history.push(`/resources/new?type=${option.value}`)}
              /* Re-picking the same type pushed the page it was already on — a
                 duplicate history entry, so browser Back seemed to do nothing.
                 A different type replaces the entry: Back from the editor goes
                 to where the user came from, not through every type tried. */
              onChange={(option) => {
                if (option.value !== activeTypeKey) history.replace(`/resources/new?type=${option.value}`);
              }}
            />

            {/* The picker and its message share a column, so the reason sits
                under the control rather than only colouring its border — a red
                outline on its own does not say what is wrong with it. */}
            <div className="rsce-app-field">
              <Select
                className="rsce-app-pick"
                options={appOptions}
                value={appId ? { label: "", value: appId } : null}
                placeholder="Application name..."
                error={appMissing}
                disabled={isReadOnly}
                onChange={(option) => setAppId(option.value)}
              />
              {appMissing && <span className="rsce-error-text">An application is required.</span>}
            </div>
          </div>
        </div>

        <div className="rsce-split">
          {/* Creating, the sidebar is the batch being built — for connections
              as much as for HTTP resources. Editing, it lists what already
              exists and is a way to move between records. */}
          {/* Editing uses the drafts sidebar too, with its one record. The list
              mode below only displays a name, and an HTTP resource has no other
              name field — so while editing its name could not be changed at all.
              Drafts mode gives the same inline rename (pencil), duplicate check
              and error as creating. */}
          <EditorSidebar
            mode="drafts"
            apps={apps}
            appId={appId}
            onAppChange={setAppId}
            drafts={items}
            nameStatus={nameStatus}
            appMissing={appMissing}
            selectedDraftId={activeItem?.id}
            onSelectDraft={selectItem}
            onAddDraft={addItem}
            onRenameDraft={renameItem}
            onDeleteDraft={
              isEdit
                ? () =>
                    existing &&
                    setPendingDelete({
                      kind: "existing",
                      resource: existing,
                      label: existing.name,
                      saved: [existing.id],
                    })
                : requestDeleteItem
            }
            showSearch={!isEdit}
            canAdd={!isEdit}
            draftsAreSaved={isEdit}
            readOnly={isReadOnly}
          />
          {/* PREVIOUS — list-mode sidebar while editing. Kept per house convention.
          {!isEdit ? (
            <EditorSidebar
              mode="drafts"
              apps={apps}
              appId={appId}
              onAppChange={setAppId}
              drafts={items}
              nameStatus={nameStatus}
              appMissing={appMissing}
              selectedDraftId={activeItem?.id}
              onSelectDraft={selectItem}
              onAddDraft={addItem}
              onRenameDraft={renameItem}
              onDeleteDraft={requestDeleteItem}
            />
          ) : (
            <EditorSidebar
              mode="list"
              apps={apps}
              appId={appId}
              onAppChange={setAppId}
              items={sidebarItems}
              appMissing={appMissing}
              selectedItemId={resourceId}
              (One row, already open — nothing to search through, and nothing
               to add without leaving the resource being edited.)
              showSearch={false}
              canAdd={false}
              onSelectItem={(item) => {
                // A different record is a different page, so the form re-seeds from it.
                seededFor.current = null;
                history.push(`/resources/${item.id}/edit`);
              }}
              onAddItem={() => {
                seededFor.current = null;
                history.push(`/resources/new?type=${activeTypeKey}`);
              }}
              onDeleteItem={(item) =>
                setPendingDelete({
                  kind: "existing",
                  resource: item.resource,
                  label: item.name,
                  saved: [item.id],
                })
              }
            />
          )}
          */}

          <main className="rsce-main">
            {layout === "http"
              ? activeItem && (
                  <HttpResourceForm
                    endpoint={activeItem}
                    onChange={patchItem}
                    onTest={handleTest}
                    test={test}
                    errors={activeItemErrors}
                    readOnly={isReadOnly}
                  />
                )
              : activeItem && (
                  <DbResourceForm
                    schema={schema}
                    connection={activeItem}
                    onChange={patchItem}
                    test={test}
                    errors={activeConnectionErrors}
                    isEdit={isEdit}
                    nameStatus={nameStatus}
                    readOnly={isReadOnly}
                  />
                )}

            <div className="rsce-footer">
              {layout === "db" && (
                <button
                  type="button"
                  className="rsce-btn rsce-btn-outline"
                  disabled
                  title="A database cannot be reached from the browser — this needs a server-side test endpoint."
                >
                  Test Connection
                </button>
              )}
              {/* Always clickable: pressing it is how the user asks to be told
                  what is wrong, and every problem is then marked on the field it
                  belongs to. A disabled button would leave a problem in a
                  resource they are not looking at with no way to surface it.

                  The label counts, because pressing it creates that many
                  separate resources and that should not be a surprise. */}
              {/* <button type="button" className="rsce-btn rsce-btn-primary" disabled={saving} onClick={save}> */}
              {/* Editing: nothing to update until something differs from what was
                  loaded (see isDirty). Creating is unaffected — the button stays
                  enabled so pressing it can surface what is missing, as above. */}
              {/* Not in View mode — the header's Edit button leads to it. */}
              {!isReadOnly && (
                <button
                  type="button"
                  className="rsce-btn rsce-btn-primary"
                  disabled={saving || !isDirty}
                  title={!isDirty ? "No changes to update" : undefined}
                  onClick={save}
                >
                  {saving
                    ? "Saving…"
                    : isEdit
                      ? "Update Resource"
                      : items.length > 1
                        ? `Create ${items.length} Resources`
                        : "Create Resource"}
                </button>
              )}
            </div>
          </main>
        </div>

        <ConfirmDialog
          open={Boolean(pendingDelete)}
          danger
          title={pendingDelete?.kind === "draft" ? "Remove this resource?" : "Delete this resource?"}
          message={
            pendingDelete?.kind === "draft"
              ? `“${pendingDelete?.label}” has not been created yet, so this only clears what you have filled in.`
              : `“${pendingDelete?.label}” will be deleted permanently, and any report using it stops working.`
          }
          confirmText="Delete"
          onCancel={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
        />
      </div>
    </DashboardView>
  );
}
